import React, { useEffect, useState } from 'react';
import { Collapse, DatePicker, Pagination, Radio, Select } from 'antd';
import moment from 'moment';
import { Button, Icons, Input, Notification, PageTitle, Tooltip } from '~/components';
import { fileManagerPaiDownload } from '~/services/FileManager/file-manager.service';
import * as ModuloService from '../../../services/modulos.service';
import { CHAVES_MODULO } from '../../Helper/chave-modulo-constants';
import {
  baixarFicha,
  camposIdentificacao,
  formatarData,
  formatarTamanho,
  mensagemSemIdentificacao,
  nomeArquivoExibicao,
  subtitulo,
} from '../helpers/buscaDocumentos.helper';
import { buscarDocumentos, obterModulosBusca } from '../services/buscaDocumentos.service';
import * as S from '../buscaDocumentos.styled';

const TAMANHOS = [10, 20, 30];

function diaFuturo(data) {
  return Boolean(data && data.isAfter(moment(), 'day'));
}

function Identificacao({ item }) {
  const campos = camposIdentificacao(item);
  return (
    <S.Ficha>
      <S.FichaTitulo>
        <Icons.ProfileOutlined /> Identificação
      </S.FichaTitulo>
      {campos.length === 0 ? (
        <S.Vazio>{mensagemSemIdentificacao}</S.Vazio>
      ) : (
        campos.map(campo => (
          <S.Dado key={campo.rotulo}>
            {campo.rotulo}
            <strong>{campo.valor}</strong>
          </S.Dado>
        ))
      )}
    </S.Ficha>
  );
}

function NomeDoArquivo({ item }) {
  const nome = nomeArquivoExibicao(item.NomeArquivo);
  return (
    <S.NomeArquivo>
      Nome do arquivo
      <Tooltip title={nome || undefined}>
        <span>{nome}</span>
      </Tooltip>
    </S.NomeArquivo>
  );
}

function mensagem(error) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data) return data;
  return data?.Message || data?.message || 'Não foi possível buscar os documentos.';
}

async function abrirArquivo(urlArquivo) {
  try {
    await fileManagerPaiDownload(urlArquivo);
  } catch {
    Notification.showError('Não foi possível abrir o arquivo. Tente novamente.');
  }
}

function BuscaDocumentos() {
  const [modulos, setModulos] = useState([]);
  const [termo, setTermo] = useState('');
  const [periodo, setPeriodo] = useState('documento');
  const [inicio, setInicio] = useState(null);
  const [fim, setFim] = useState(null);
  const [chaveModulo, setChaveModulo] = useState(undefined);
  const [resposta, setResposta] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(10);

  useEffect(() => {
    ModuloService.verificaModuloHabilitado(CHAVES_MODULO.BUSCA_DOCUMENTOS);
    obterModulosBusca()
      .then(response => setModulos(response?.data || []))
      .catch(() => setModulos([]));
  }, []);

  function limpar() {
    setTermo('');
    setPeriodo('documento');
    setInicio(null);
    setFim(null);
    setChaveModulo(undefined);
    setResposta(null);
    setPagina(1);
    setTamanho(10);
  }

  async function buscar() {
    const temPeriodo = Boolean(inicio || fim);
    if (!termo.trim() && !temPeriodo) {
      Notification.showError('Informe um termo ou um período.');
      return;
    }

    setBuscando(true);
    try {
      const response = await buscarDocumentos({
        Termo: termo.trim(),
        Periodo: temPeriodo ? periodo : null,
        Inicio: inicio ? inicio.format('YYYY-MM-DD') : null,
        Fim: fim ? fim.format('YYYY-MM-DD') : null,
        ChaveModulo: chaveModulo || null,
      });
      setResposta(response?.data || { Quantidade: 0, Itens: [] });
      setPagina(1);
    } catch (error) {
      Notification.showError(mensagem(error));
    } finally {
      setBuscando(false);
    }
  }

  const itens = resposta?.Itens || [];
  const quantidade = resposta?.Quantidade || 0;
  const visiveis = itens.slice((pagina - 1) * tamanho, pagina * tamanho);

  return (
    <>
      <PageTitle titulo="Busca de Documentos" />
      <S.Pagina>
        <S.Painel>
          <S.CampoTermo>
            Termo de busca
            <Input
              value={termo}
              prefix={<Icons.SearchOutlined />}
              placeholder="Título, tipo, número ou texto do arquivo"
              onChange={event => setTermo(event.target.value)}
              onPressEnter={buscar}
            />
          </S.CampoTermo>
          <S.Filtros>
            <S.Campo>
              <S.Rotulo>
                <Icons.CalendarOutlined /> Período
              </S.Rotulo>
              <S.Periodo>
                <Radio.Group
                  value={periodo}
                  buttonStyle="solid"
                  onChange={event => setPeriodo(event.target.value)}
                >
                  <Radio.Button value="documento">Data do documento</Radio.Button>
                  <Radio.Button value="atualizacao">Data da atualização</Radio.Button>
                </Radio.Group>
                <DatePicker
                  value={inicio}
                  format="DD/MM/YYYY"
                  placeholder="De"
                  disabledDate={diaFuturo}
                  onChange={setInicio}
                />
                <DatePicker
                  value={fim}
                  format="DD/MM/YYYY"
                  placeholder="Até"
                  disabledDate={diaFuturo}
                  onChange={setFim}
                />
              </S.Periodo>
            </S.Campo>
            <S.Campo>
              <S.Rotulo>
                <Icons.AppstoreOutlined /> Módulo
              </S.Rotulo>
              <S.SelectAncora>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder="Todos os módulos"
                  value={chaveModulo}
                  style={{ width: '100%' }}
                  getPopupContainer={trigger => trigger.parentNode}
                  onChange={setChaveModulo}
                >
                  {modulos.map(modulo => (
                    <Select.Option key={modulo.ChaveModulo} value={modulo.ChaveModulo}>
                      {modulo.Nome}
                    </Select.Option>
                  ))}
                </Select>
              </S.SelectAncora>
            </S.Campo>
            <S.Acoes>
              <Button onClick={limpar}>Limpar</Button>
              <Button type="primary" loading={buscando} onClick={buscar}>
                <Icons.SearchOutlined /> Buscar
              </Button>
            </S.Acoes>
          </S.Filtros>
        </S.Painel>

        {resposta && (
          <>
            <S.Contagem>
              {quantidade === 1 ? '1 documento encontrado' : `${quantidade} documentos encontrados`}
              {quantidade > itens.length ? ` · mostrando ${itens.length}` : ''}
            </S.Contagem>
            <S.Lista>
              {visiveis.map(item => (
                <S.Cartao key={`${item.ChaveModulo}-${item.Id}`}>
                  <Collapse bordered={false}>
                    <Collapse.Panel
                      header={
                        <S.Cabecalho>
                          <S.Selo>{(item.Extensao || 'ARQ').toUpperCase()}</S.Selo>
                          <S.Textos>
                            <S.Nome>{item.Titulo}</S.Nome>
                            <S.Linha>{subtitulo(item)}</S.Linha>
                          </S.Textos>
                        </S.Cabecalho>
                      }
                      extra={
                        <S.AcoesItem onClick={event => event.stopPropagation()}>
                          <Tooltip title="Exportar metadados">
                            <S.Acao type="button" aria-label="Exportar metadados" onClick={() => baixarFicha(item)}>
                              <Icons.FileTextOutlined />
                            </S.Acao>
                          </Tooltip>
                          {item.UrlArquivo ? (
                            <Tooltip title="Baixar arquivo">
                              <S.AcaoDownload
                                type="button"
                                aria-label="Baixar arquivo"
                                onClick={() => abrirArquivo(item.UrlArquivo)}
                              >
                                <Icons.DownloadOutlined />
                              </S.AcaoDownload>
                            </Tooltip>
                          ) : null}
                        </S.AcoesItem>
                      }
                    >
                      <S.Fichas>
                        <Identificacao item={item} />
                        <S.Ficha>
                          <S.FichaTitulo>
                            <Icons.FileTextOutlined /> Metadados do arquivo
                          </S.FichaTitulo>
                          <NomeDoArquivo item={item} />
                          <S.Par>
                            <S.Dado>
                              Tamanho
                              <strong>{formatarTamanho(item.TamanhoBytes)}</strong>
                            </S.Dado>
                            <S.Dado>
                              Versão
                              <strong>{item.NumeroVersao != null ? `v${item.NumeroVersao}` : ''}</strong>
                            </S.Dado>
                          </S.Par>
                          <S.Dado>
                            Data de inclusão
                            <strong>{formatarData(item.DataInclusao, true)}</strong>
                          </S.Dado>
                        </S.Ficha>
                        <S.Ficha>
                          <S.FichaTitulo>
                            <Icons.CalendarOutlined /> Última atualização
                          </S.FichaTitulo>
                          <S.Dado>
                            Data e hora
                            <strong>{formatarData(item.DataUltimaAtualizacao, true)}</strong>
                          </S.Dado>
                        </S.Ficha>
                      </S.Fichas>
                    </Collapse.Panel>
                  </Collapse>
                </S.Cartao>
              ))}
            </S.Lista>
            {itens.length > 0 && (
              <S.Paginacao>
                <S.Tamanho>
                  <Select
                    value={tamanho}
                    getPopupContainer={trigger => trigger.parentNode}
                    onChange={valor => {
                      setTamanho(valor);
                      setPagina(1);
                    }}
                  >
                    {TAMANHOS.map(opcao => (
                      <Select.Option key={opcao} value={opcao}>
                        {opcao}
                      </Select.Option>
                    ))}
                  </Select>
                </S.Tamanho>
                <Pagination
                  current={pagina}
                  pageSize={tamanho}
                  total={itens.length}
                  showSizeChanger={false}
                  onChange={setPagina}
                />
              </S.Paginacao>
            )}
          </>
        )}
      </S.Pagina>
    </>
  );
}

export default BuscaDocumentos;
